<template>
  <div
    v-if="groupCoaching.registered"
    class="i-jca-join"
  >
    <div
      v-if="$props.showHeader"
      class="description"
    >
      <div class="header">Your Next Session</div>
      <div class="sub">Ready to join your cohort? Here's what's coming up this week.</div>
    </div>
    <div class="card">
      <div class="details">
        <div class="head">
          <img :src="iconCalendar" /> {{ groupCoaching.startTime }}
          <div
            v-if="meetingInProgress && isMdOrLarger && !progress.hide"
            class="badge"
          >
            <div class="circle"></div>
            In Progress
          </div>
        </div>
        {{ groupCoaching.topic }}
      </div>
      <div
        v-if="meetingInProgress && !progress.hide"
        class="progress"
      >
        <div class="text">
          <div class="key">Meeting Progress</div>
          <div>
            {{
              Math.floor(((MEETING_DURATION_IN_MS - Math.abs(progress.meetingTimeDifferenceInMS)) / 60 / 1000) % 60)
            }}m {{ Math.floor(((MEETING_DURATION_IN_MS - Math.abs(progress.meetingTimeDifferenceInMS)) / 1000) % 60) }}s
            remaining
          </div>
        </div>
        <iBaseLoadingBar
          :fill-color="bilbao600"
          bar-width="100%"
          bar-height="10px"
          :infinite="false"
          :is-active="meetingInProgress"
          :start="`${progress.loaderPercent}%`"
          :loading-animation-duration-ms="MEETING_DURATION_IN_MS"
        />
        <div class="note">
          <div>{{ progress.loaderPercent }}% complete</div>
          <div>{{ MEETING_DURATION_IN_MINS }} minutes total</div>
        </div>
      </div>
      <div class="add-cal">
        <div
          v-if="hasCalendarLinks"
          class="cta"
        >
          <iBaseButton
            class="more"
            v-bind="CAL_BUTTON_STYLE"
          />
          <div class="wrapper">
            <div class="options">
              <div
                v-for="cta in mainCtaData"
                :key="cta.label"
                class="option"
                @click="openUrl(cta.url)"
              >
                <img :src="cta.icon" />
                {{ cta.label }}
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="!meetingDue"
          class="link"
        >
          <img
            alt="Zoom icon"
            :src="iconZoomGrey"
          />
          Link would be available 15 mins before meeting
        </div>
        <iBaseButton
          v-else
          v-bind="ZOOM_BUTTON_STYLE"
          @clicked="openUrl(groupCoaching.joinUrl)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, watch } from "vue"
import { useUserStore } from "@/stores/user"
import { azureRadiance700, bilbao600, bluePurpleGradient, white } from "@/styles/colors"
import { breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import { GlobalFunctions } from "@/helpers/globalFunctions"
import api from "@/services/apiClient"

import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseLoadingBar from "@/components/base/BaseLoadingBar.vue"

// ASSETS
import iconDotsHorizontal from "@/assets/icons/icon-dots-horizontal.svg"
import iconChevron from "@/assets/icons/icon-chevron-down-white.svg"
import iconCalendarWhite from "@/assets/icons/icon-calendar-white.svg"
import iconCalendar from "@/assets/icons/icon-calendar.svg"
import iconIosCalendar from "@/assets/icons/icon-ios-calendar.svg"
import iconOutlookCalendar from "@/assets/icons/icon-outlook-calendar.svg"
import iconGoogleCalendar from "@/assets/icons/icon-google-calendar.svg"
import iconZoom from "@/assets/icons/icon-zoom.svg"
import iconZoomGrey from "@/assets/icons/icon-zoom-grey.svg"

defineProps({
  showHeader: {
    type: Boolean,
    default: true
  }
})

// STORES AND COMPOSABLES
const userStore = useUserStore()
const isMdOrLarger = useIsScreenSize(breakpointMd)

// CONSTANTS
const MEETING_DAY_OF_WEEK = 4 // Thursday
const MEETING_DURATION_IN_MINS = 60
const MEETING_DURATION_IN_MS = MEETING_DURATION_IN_MINS * 60 * 1000
const DATE_STRING_OPTIONS = {
  weekday: "long",
  month: "long",
  day: "numeric",
  timeZoneName: "short",
  hour: "2-digit",
  minute: "2-digit"
} as const
const CAL_BUTTON_STYLE = {
  size: "small",
  fontWeight: "500",
  width: "220px",
  backgroundImage: bluePurpleGradient,
  borderRadius: "8px",
  textColor: white,
  text: "Add to Calendar",
  iconUrl: iconChevron,
  preIcon: iconCalendarWhite
}
const ZOOM_BUTTON_STYLE = {
  size: "small",
  fontWeight: "500",
  width: "220px",
  backgroundColor: white,
  version: "outline",
  borderRadius: "8px",
  textColor: azureRadiance700,
  text: "Join",
  preIcon: iconZoom
}

// DATA
const groupCoaching = reactive({
  registered: false,
  startTime: "",
  topic: "The JobTest Career Accelerator",
  joinUrl: "",
  meetingTimeDifferenceInMins: 40 // Random number higher than 15 mins
})
const calendarLinks = reactive({
  googleCalendarUrl: "",
  outlookCalendarUrl: "",
  httpsUrl: "",
  webcalUrl: ""
})
const progress = reactive({
  meetingTimeDifferenceInMS: 1000000, // Random number higher than 900,000ms
  loaderPercent: 0,
  hide: false
})

// COMPUTED
const meetingDue = computed(
  () =>
    groupCoaching.meetingTimeDifferenceInMins <= 15 &&
    groupCoaching.meetingTimeDifferenceInMins > -MEETING_DURATION_IN_MINS
)
const meetingInProgress = computed(
  () =>
    groupCoaching.meetingTimeDifferenceInMins <= 0 &&
    groupCoaching.meetingTimeDifferenceInMins > -MEETING_DURATION_IN_MINS
)
const hasCalendarLinks = computed(() => {
  return !!(
    calendarLinks.googleCalendarUrl ||
    calendarLinks.outlookCalendarUrl ||
    calendarLinks.httpsUrl ||
    calendarLinks.webcalUrl
  )
})
const mainCtaData = computed(() => {
  const ctaData = [
    {
      label: "Google Calendar",
      icon: iconGoogleCalendar,
      url: calendarLinks.googleCalendarUrl
    },
    {
      label: "Apple Calendar",
      icon: iconIosCalendar,
      url: calendarLinks.webcalUrl
    },
    {
      label: "Outlook Calendar",
      icon: iconOutlookCalendar,
      url: calendarLinks.outlookCalendarUrl
    },
    {
      label: "Others",
      icon: iconDotsHorizontal,
      url: calendarLinks.httpsUrl
    }
  ]

  return ctaData.filter((e) => !!e.url)
})

// FUNCTIONS
/**
 * Gets the next meeting date at 2PM EST using MEETING_DAY_OF_WEEK and in user's timezone
 */
const defaultNextDate = () => {
  const day = new Date()
  const days = (day.getDay() < MEETING_DAY_OF_WEEK ? 0 : 7) - day.getDay() + MEETING_DAY_OF_WEEK
  const defaultNextDate = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate() + days, 18, 0, 0))

  return defaultNextDate
}
const fetchGroupCoachingData = async () => {
  if (userStore.getUserId) {
    try {
      const groupCoachingData = await api.client.checkGroupCoaching(userStore.getUserId)
      groupCoaching.registered = groupCoachingData.registered

      if (groupCoaching.registered) {
        const dateToUse = groupCoachingData.startTime ? new Date(groupCoachingData.startTime) : defaultNextDate()
        groupCoaching.startTime = dateToUse.toLocaleString("en-US", DATE_STRING_OPTIONS)
        groupCoaching.topic = groupCoachingData.topic || groupCoaching.topic
        groupCoaching.joinUrl = groupCoachingData.joinUrl || ""

        const currentDateTime = new Date()
        progress.meetingTimeDifferenceInMS = dateToUse.getTime() - currentDateTime.getTime()
        groupCoaching.meetingTimeDifferenceInMins = (dateToUse.getTime() - currentDateTime.getTime()) / (60 * 1000)
      }
    } catch (error) {
      console.error("Error fetching user's group coaching status and data:", error)
    }
  }
}
const fetchCalendarLinks = async () => {
  try {
    const response = userStore.getUserId ? await api.calendar.getGroupCoachingLinks(userStore.getUserId) : null
    if (response) {
      calendarLinks.googleCalendarUrl = response.googleCalendarUrl
      calendarLinks.webcalUrl = response.webcalUrl
      calendarLinks.outlookCalendarUrl = response.outlookCalendarUrl
      calendarLinks.httpsUrl = response.httpsUrl
    }
  } catch (error) {
    console.error("Error fetching user's calendar links:", error)
  }
}
const openUrl = (redirectUrl: string) => {
  window.open(redirectUrl, "_blank")
}
const startLoader = async () => {
  progress.loaderPercent = Math.round((Math.abs(progress.meetingTimeDifferenceInMS) * 100) / MEETING_DURATION_IN_MS)

  // Load 100% for loading bar
  while (progress.loaderPercent < 100) {
    await GlobalFunctions.delayInMiliseconds(MEETING_DURATION_IN_MS / 100)
    progress.loaderPercent++
    progress.meetingTimeDifferenceInMS = (progress.loaderPercent / 100) * MEETING_DURATION_IN_MS
  }

  progress.hide = true
}

// LIFECYCLE HOOKS
onMounted(async () => {
  await fetchGroupCoachingData()
  await fetchCalendarLinks()
})

// WATCHERS
watch(
  () => meetingInProgress.value, // Watch for changes to meetingInProgress
  () => {
    if (meetingInProgress.value) {
      startLoader()
    }
  }
)
</script>

<style scoped lang="scss">
.i-jca-join {
  @include flex(column, start, center, $gap: 30px);

  width: 100%;

  @media screen and (min-width: $breakpoint-md) {
    gap: 40px;
  }

  .description {
    @include flex(column, start, center, $gap: 12px);

    text-align: center;

    .header {
      @include display-sm;

      font-weight: 700;
    }

    .sub {
      @include text-lg;

      max-width: 860px;
      color: $black700;
    }
  }

  .card {
    @include flex(column, start, center, $gap: 20px);
    @include border-radius-md;

    background-color: white;
    box-shadow: 0 4px 16px 0 $black100;
    padding: 16px;
    text-align: center;
    width: 100%;

    @media screen and (min-width: $breakpoint-xs) {
      padding: 20px;
    }

    @media screen and (min-width: $breakpoint-lg) {
      padding: 24px;
      max-width: 798px;
    }

    .details {
      @include flex(column, start, center, $gap: 8px);

      font-weight: 500;

      .head {
        @media screen and (min-width: $breakpoint-xs) {
          @include flex(row, center, center, $gap: 10px);
        }

        .badge {
          @include flex(row, start, center, $gap: 8px);
          @include border-radius-lg;
          @include text-xs;

          font-weight: 600;
          background-color: $bilbao50;
          padding: 2px 12px;
          border: 1px solid $bilbao500;
          color: $bilbao500;

          .circle {
            @include border-radius-rounded;

            width: 7px;
            height: 7px;
            flex: 0 0 7px;
            background-color: $bilbao500;
          }
        }
      }
    }

    .progress {
      @include flex(column, start, stretch, $gap: 8px);

      width: 100%;
      max-width: 456px;

      .text {
        @include flex(row, space-between, center, $gap: 10px);
        @include text-xs;

        color: $bilbao500;

        .key {
          color: $bilbao700;
          font-weight: 600;
        }
      }

      .note {
        @include flex(row, space-between, center, $gap: 10px);
        @include text-xxs;
      }
    }

    .add-cal {
      @include flex(column, start, center, $gap: 16px);

      @media screen and (min-width: $breakpoint-md) {
        @include flex(row, center, center, $gap: 16px);
      }

      .cta {
        position: relative;
        width: fit-content;

        .wrapper {
          display: none;
          position: absolute;
          min-width: max-content;
          top: calc(100% - 4px);
          z-index: 1;

          &:hover {
            display: block;
          }

          .options {
            @include border-radius-sm;
            @include flex(column, start, stretch);

            overflow: hidden;
            border: 1px solid $zumthor100;
            box-shadow: 0 4px 8px 0 $black400;
            background-color: $white;
            margin-top: 10px;
            padding: 5px 0;
            width: 220px;

            .option {
              @include flex(row, start, center, $gap: 8px);

              padding: 6px 12px;
              cursor: pointer;

              &:hover {
                background-color: $black50;
              }

              &:not(:last-child) {
                border-bottom: 1px solid $zumthor100;
              }
            }
          }
        }

        .more:hover + .wrapper {
          display: block;
        }
      }

      .link {
        @include flex(row, start, center, $gap: 5px);
        @include text-xs;

        color: $black500;
        font-weight: 500;

        @media screen and (min-width: $breakpoint-xs) {
          gap: 8px;
        }

        img {
          width: 18px;

          @media screen and (min-width: $breakpoint-xs) {
            width: 21px;
          }
        }
      }
    }
  }
}
</style>
